import { type FormEventHandler, memo } from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { GdprBeforeOfferPrivacyLink } from '@/components/GdprBeforeOfferPrivacyLink'
import { PersonalNumberField } from '@/components/PersonalNumberField/PersonalNumberField'
import { useTryConfirmPriceIntentWithSuggestedData } from '@/features/priceCalculator/PurchaseForm/InsuranceDataForm/useTryConfirmPriceIntentWithSuggestedData'
import { GOTO_NEXT_SECTION, useActiveFormSectionId } from '@/features/priceCalculator/useActiveFormSectionId'
import { usePriceIntentValueOrThrow } from '@/features/priceCalculator/usePriceIntent'
import { useShopSessionCustomer } from '@/features/priceCalculator/useShopSessionCustomer'
import { EmploymentType, PriceIntentDocument, useShopSessionCustomerUpdateMutation } from '@/services/graphql/generated'
import { logger } from '@/services/logger/logger.client'
import { useShopSessionId } from '@/services/shopSession/ShopSessionContext'
import { useErrorMessage } from '@/utils/useErrorMessage'
import { Button } from 'ui/src/components/Button/Button'
import { yStack } from 'ui/src/patterns/stack'

const SsnFieldName = 'ssn'

export const SsnSeSectionV2 = memo(() => {
  const shopSessionId = useShopSessionId()!
  const shopSessionCustomer = useShopSessionCustomer()
  const { setActiveFormSectionId } = useActiveFormSectionId()
  const priceIntent = usePriceIntentValueOrThrow()

  const goToNextSection = () => {
    setActiveFormSectionId(GOTO_NEXT_SECTION)
    // Scroll to top when going to next step to avoid sticky header overlap
    window.scrollTo({ top: 0, behavior: 'instant' })
  }

  const tryConfirmPriceIntentWithSuggestedData = useTryConfirmPriceIntentWithSuggestedData()
  const [updateCustomer, result] = useShopSessionCustomerUpdateMutation({
    // priceIntent.suggestedData may be updated based on customer.ssn
    refetchQueries: [PriceIntentDocument],
    awaitRefetchQueries: true,
    onCompleted() {
      // Payment Protection is fully derivable from the SSN lookup: apply the suggested
      // address and confirm right away, falling back to the address section otherwise.
      if (priceIntent.product.name === 'SE_PAYMENT_PROTECTION') {
        tryConfirmPriceIntentWithSuggestedData(priceIntent, {
          // Updating employment type before confirming the price intent allows a default value
          // for a field that otherwise requires user input
          additionalData: { employmentType: EmploymentType.PermanentEmployedFulltime },
          fallback: goToNextSection,
        })
        return
      }

      goToNextSection()
    },
    onError(error) {
      logger.debug(`Failed to update customer ssn: ${error.message}`, { error })
    },
  })

  const handleSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault()
    const ssn = event.currentTarget[SsnFieldName].value
    if (typeof ssn !== 'string') throw new Error('No SSN found in SSN section form')
    updateCustomer({ variables: { input: { shopSessionId: shopSessionId, ssn } } })
  }

  const { t } = useTranslation('purchase-form')
  const errorMessage = useErrorMessage(result.error)

  return (
    <form className={clsx(yStack({ gap: { _: 'md', lg: 'xs' } }))} onSubmit={handleSubmit}>
      <PersonalNumberField
        label={t('FIELD_SSN_SE_LABEL')}
        name={SsnFieldName}
        defaultValue={shopSessionCustomer?.ssn ?? ''}
        required={true}
        warning={!!errorMessage}
        message={errorMessage}
      />

      <Button type="submit" size="large" loading={result.loading} fullWidth={true}>
        {t('SUBMIT_LABEL_PROCEED')}
      </Button>

      <GdprBeforeOfferPrivacyLink />
    </form>
  )
})
SsnSeSectionV2.displayName = 'SsnSeSectionV2'

export const SSN_SE_SECTION_ID = 'ssn-se'
